import { Injectable } from '@angular/core';
import { Actions, ofActionDispatched, Select, Store } from '@ngxs/store';
import { AudienceResource } from 'app/library/grow/models/audience-resource.model';
import { NgxsActionHelper } from 'app/projects/core/src/lib/services/action.helper';
import { MaxBrainUtils } from 'app/projects/core/src/lib/utils';
import { BehaviorSubject, Observable, firstValueFrom, race } from 'rxjs';
import { map, take } from 'rxjs/operators';
import {
    CreateAudience,
    CreateAudienceSuccess,
    DeleteAudience,
    DeleteAudienceSuccess,
    FetchAudienceChildren,
    FetchAudienceChildrenFailure,
    FetchAudienceChildrenSuccess,
    FetchAudienceRelevantExperiences,
    FetchAudienceRelevantExperiencesFailure,
    FetchAudienceRelevantExperiencesSuccess,
    FetchAudiences,
    FetchAudiencesFailure,
    FetchAudiencesSuccess,
    FetchAudienceWidgets,
    FetchAudienceWidgetsFailure,
    FetchAudienceWidgetsSuccess,
    FetchDefaultAudiences,
    FetchDefaultAudiencesFailure,
    FetchDefaultAudiencesSuccess,
    MoveToAudience,
    MoveToAudienceSuccess,
    UpdateAudience,
    UpdateAudienceExperienceStatus,
    UpdateAudienceExperienceStatusFailure,
    UpdateAudienceExperienceStatusSuccess,
    UpdateAudienceFailure,
    UpdateAudienceSuccess,
} from '../actions/index';
import { AudienceWidget } from '../models/audience-widget.model';
import { MaxBrainAudience } from '../models/audience.model';
import { AudienceState } from '../models/audience.state';
import { AudiencesState } from '../models/audiences.state';
import { AudienceRelevantExperience } from 'app/library/grow/models/audience-relevant-experience.model';
import { IAudienceFilters } from 'app/library/grow/interfaces/audience-filters.interface';
import { IAudienceFiltersPayload } from 'app/library/grow/interfaces/audience-filters-payload.interface';
import { IAudienceMembersRequestOptions } from 'app/library/grow/interfaces/audience-members-request.interface';
import { IRemoveAudienceUsersRequest } from 'app/library/audience-user/interfaces/remove-audience-users-request.interface';
import { AudienceTypeEnum } from 'app/library/grow/enums/audience-type.enum';
import {
    AUDIENCES_CATEGORY_NAME_KEY,
    compareGrowAudienceSidebarChildren,
    GROW_SIDEBAR_AUDIENCES_CATEGORY_ID,
    isAutomaticAudiencesCategory,
    isCourseAudiencesCategory,
} from 'app/library/grow/utils/audience-display.util';
import { AudienceExperienceStatusEnum } from '../enums/audience-experience-status.enum';
import { AudienceApiService, AudienceListRequestOptions, EXCLUDE_AUTOMATIC_AUDIENCE_LIST_OPTIONS } from './audience.api-service';

@Injectable()
export class AudienceService {
    @Select(AudienceState.getEntities)
    audiences$: Observable<MaxBrainAudience[]>;

    @Select(AudiencesState.getFilteredEntities)
    filteredAudience$: Observable<MaxBrainAudience[]>;

    @Select(AudiencesState.getDefaultAudiences)
    defaultAudiences$: Observable<MaxBrainAudience[]>;

    @Select(AudiencesState.getDefaultAudiencesTotalCount)
    defaultAudiencesTotalCount$: Observable<number>;

    private _newAudienceSubject = new BehaviorSubject<MaxBrainAudience>(null);
    public newAudienceSubject$ = this._newAudienceSubject.asObservable();

    private _removeAudienceSubject = new BehaviorSubject<string>(null);
    public removeAudienceSubject$ = this._removeAudienceSubject.asObservable();

    private _growSidebarAudienceRefreshSubject = new BehaviorSubject<MaxBrainAudience>(null);
    public growSidebarAudienceRefresh$ = this._growSidebarAudienceRefreshSubject.asObservable();

    constructor(private _store: Store, private _action$: Actions, private _actionHelper: NgxsActionHelper, private _audienceApiService: AudienceApiService) {}

    async notifyGrowSidebarAudienceRefresh(audienceId: string): Promise<void> {
        try {
            const audience = await firstValueFrom(this._audienceApiService.read(audienceId));
            this._growSidebarAudienceRefreshSubject.next(audience);
        } catch {
            // Sidebar refresh is best-effort after filter save
        }
    }

    getAudiences(): MaxBrainAudience[] {
        return this._store.selectSnapshot(AudiencesState.getFilteredEntities);
    }

    getAudienceById(id: string): MaxBrainAudience {
        return AudienceState.getEntity(this._store.selectSnapshot(AudienceState), id);
    }

    getDefaultAudiences(): MaxBrainAudience[] {
        return this._store.selectSnapshot(AudiencesState.getDefaultAudiences);
    }

    async fetchAudiences(search?: string): Promise<boolean> {
        this._store.dispatch(new FetchAudiences({ search }));

        return await this._actionHelper.race(FetchAudiencesSuccess, FetchAudiencesFailure).toPromise();
    }

    fetchAudiencesList(search?: string, listOptions?: AudienceListRequestOptions): Promise<MaxBrainAudience[]> {
        return firstValueFrom(this._audienceApiService.getAll(search, listOptions));
    }

    fetchAudienceChildrenList(audienceId: string, listOptions?: AudienceListRequestOptions): Promise<MaxBrainAudience[]> {
        return firstValueFrom(this._audienceApiService.getAudienceChildren(audienceId, listOptions));
    }

    async fetchAudienceChildren(audienceId: string): Promise<MaxBrainAudience[]> {
        this._store.dispatch(new FetchAudienceChildren(audienceId));

        return race(
            this._action$.pipe(
                ofActionDispatched(FetchAudienceChildrenSuccess),
                map((action: FetchAudienceChildrenSuccess) => action.payload)
            ),
            this._action$.pipe(
                ofActionDispatched(FetchAudienceChildrenFailure),
                map((action: FetchAudienceChildrenFailure) => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    async createAudience(audience: MaxBrainAudience) {
        this._store.dispatch(new CreateAudience(audience));

        this._action$.pipe(ofActionDispatched(CreateAudienceSuccess), take(1)).subscribe((action: CreateAudienceSuccess) => {
            this._newAudienceSubject.next(action.payload);
        });
    }

    async updateAudience(audience: MaxBrainAudience): Promise<boolean> {
        this._store.dispatch(new UpdateAudience(audience));

        return await this._actionHelper.race(UpdateAudienceSuccess, UpdateAudienceFailure).toPromise();
    }

    moveToAudience(current: MaxBrainAudience, target: MaxBrainAudience) {
        this._store.dispatch(new MoveToAudience({ current, target }));

        this._action$.pipe(ofActionDispatched(MoveToAudienceSuccess), take(1)).subscribe((action: MoveToAudienceSuccess) => {
            this._newAudienceSubject.next(action.payload);
        });
    }

    deleteAudience(audienceId: string) {
        this._store.dispatch(new DeleteAudience(audienceId));

        this._action$.pipe(ofActionDispatched(DeleteAudienceSuccess), take(1)).subscribe((action: DeleteAudienceSuccess) => {
            this._removeAudienceSubject.next(action.payload);
        });
    }

    async fetchAudienceWidgets(audienceId: string): Promise<AudienceWidget[]> {
        this._store.dispatch(new FetchAudienceWidgets(audienceId));

        return race(
            this._action$.pipe(
                ofActionDispatched(FetchAudienceWidgetsSuccess),
                map((action: FetchAudienceWidgetsSuccess) => action.payload)
            ),
            this._action$.pipe(
                ofActionDispatched(FetchAudienceWidgetsFailure),
                map((action: FetchAudienceWidgetsFailure) => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    async fetchAudienceRelevantExperiences(audienceId: string, status?: AudienceExperienceStatusEnum): Promise<AudienceRelevantExperience[]> {
        // When a status filter is provided, call API directly to avoid NGXS action races
        // (success/failure actions are not parameterized by filter).
        if (status != null) {
            return this._audienceApiService
                .fetchAudienceRelevantExperiences(audienceId, status)
                .pipe(take(1))
                .toPromise();
        }

        this._store.dispatch(new FetchAudienceRelevantExperiences({ audienceId }));

        return race(
            this._action$.pipe(
                ofActionDispatched(FetchAudienceRelevantExperiencesSuccess),
                map((action: FetchAudienceRelevantExperiencesSuccess) => action.payload)
            ),
            this._action$.pipe(
                ofActionDispatched(FetchAudienceRelevantExperiencesFailure),
                map((action: FetchAudienceRelevantExperiencesFailure) => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    async fetchAudienceFilters(audienceId: string): Promise<IAudienceFilters> {
        return firstValueFrom(this._audienceApiService.fetchAudienceFilters(audienceId));
    }

    async updateAudienceFilters(audienceId: string, filters: IAudienceFiltersPayload): Promise<IAudienceFilters> {
        return firstValueFrom(this._audienceApiService.updateAudienceFilters(audienceId, filters));
    }

    fetchAudienceMembers(audienceId: string, options: IAudienceMembersRequestOptions = {}) {
        return this._audienceApiService.getAudienceMembers(audienceId, options);
    }

    removeAudienceUsers(audienceId: string, payload: IRemoveAudienceUsersRequest) {
        return this._audienceApiService.removeAudienceUsers(audienceId, payload);
    }

    async updateAudienceExperienceStatus(audienceId: string, experienceId: string, status: AudienceExperienceStatusEnum): Promise<boolean> {
        this._store.dispatch(new UpdateAudienceExperienceStatus({ audienceId, experienceId, status }));

        return await this._actionHelper.race(UpdateAudienceExperienceStatusSuccess, UpdateAudienceExperienceStatusFailure).toPromise();
    }

    searchTree(audienceTree, matchingId: string) {
        if (audienceTree.id == matchingId) {
            return audienceTree;
        } else if (audienceTree.children != null) {
            var i;
            var result = null;
            for (i = 0; result == null && i < audienceTree.children.length; i++) {
                result = this.searchTree(audienceTree.children[i], matchingId);
            }
            return result;
        }
        return null;
    }

    searchTreeName(audienceTree, matchingId: string, names: string = ''): string {
        if (audienceTree.id == matchingId) {
            names = names + '/' + audienceTree.name;
            names = names.substring(1); // removes first '/' charachter
            return names.substring(names.indexOf('/') + 1); // removes default audience from a path
        } else if (audienceTree.children != null) {
            var i;
            var result = null;
            names = names + '/' + audienceTree.name;
            for (i = 0; result == null && i < audienceTree.children.length; i++) {
                result = this.searchTreeName(audienceTree.children[i], matchingId, names);
            }
            return result;
        }
        return null;
    }

    convertListToTree(list: any[]): AudienceResource[] {
        const map: Record<string, number> = {};
        const roots: any[] = [];

        let node, i;

        for (i = 0; i < list.length; i += 1) {
            map[String(list[i].id)] = i;
            list[i].children = [];
        }

        for (i = 0; i < list.length; i += 1) {
            node = list[i];
            const parentId = node.parent != null && node.parent.id != null ? String(node.parent.id) : '';
            if (parentId && parentId !== '0') {
                const parentIndex = map[parentId];
                if (parentIndex !== undefined && list[parentIndex]) {
                    list[parentIndex].children.push(node);
                } else {
                    roots.push(node);
                }
            } else {
                roots.push(node);
            }
        }
        return roots;
    }

    sortGrowAudienceChildren(audience: { children?: any[] } | null | undefined): void {
        if (!audience?.children?.length) {
            return;
        }

        audience.children.sort(compareGrowAudienceSidebarChildren);
        audience.children.forEach((child) => this.sortGrowAudienceChildren(child));
    }

    private mergeAudienceTreeChildren(existing: any[] = [], fetched: MaxBrainAudience[] = []): any[] {
        const mergedById = new Map<string, any>();

        [...existing, ...fetched].forEach((audience) => {
            const current = mergedById.get(audience.id);

            if (!current) {
                mergedById.set(audience.id, { ...audience, children: audience.children || [] });
                return;
            }

            mergedById.set(audience.id, {
                ...current,
                ...audience,
                open: current.open ?? audience.open ?? false,
                children: current.children?.length ? current.children : audience.children || [],
            });
        });

        return Array.from(mergedById.values());
    }

    private withoutPromotedSidebarCategories(audiences: any[] = []): any[] {
        return audiences.filter(
            (audience) => !isAutomaticAudiencesCategory(audience) && !isCourseAudiencesCategory(audience)
        );
    }

    private extractAutomaticAudiencesCategory(audiences: any[] = []): any | null {
        const index = audiences.findIndex((audience) => isAutomaticAudiencesCategory(audience));

        if (index < 0) {
            return null;
        }

        return audiences.splice(index, 1)[0];
    }

    private extractCourseAudiencesCategory(audiences: any[] = []): any | null {
        const index = audiences.findIndex((audience) => isCourseAudiencesCategory(audience));

        if (index < 0) {
            return null;
        }

        return audiences.splice(index, 1)[0];
    }

    private createAudiencesCategory(manualAudiences: any[]): any {
        return {
            id: GROW_SIDEBAR_AUDIENCES_CATEGORY_ID,
            name: AUDIENCES_CATEGORY_NAME_KEY,
            type: AudienceTypeEnum.NORMAL,
            children: manualAudiences.map((audience) => ({
                ...audience,
                children: audience.children || [],
                open: audience.open ?? false,
            })),
            childrenCount: manualAudiences.length,
            open: false,
            selectable: false,
            renamable: false,
            movable: false,
            deletable: false,
            isAutoGenerated: false,
            isVirtualCategory: true,
            parent: null,
        };
    }

    private async hydrateAudienceCategoryChildren(category: any): Promise<void> {
        category.open = false;

        const openById = new Map((category.children || []).map((child) => [child.id, child.open]));
        const freshChildren = (await this.fetchAudienceChildrenList(category.id)) || [];

        category.children = freshChildren.map((child) => ({
            ...child,
            open: openById.get(child.id) ?? false,
        }));
        category.childrenCount = category.children.length;
        this.sortGrowAudienceChildren(category);
    }

    private async hydrateAutomaticAudiencesCategory(automaticCategory: any): Promise<void> {
        await this.hydrateAudienceCategoryChildren(automaticCategory);
    }

    private async hydrateCourseAudiencesCategory(courseCategory: any): Promise<void> {
        await this.hydrateAudienceCategoryChildren(courseCategory);
    }

    private promoteCategoryToRoot(roots: any[], category: any, insertIndex: number): void {
        const existingIndex = roots.findIndex((root) => root.id === category.id);

        if (existingIndex >= 0) {
            roots.splice(existingIndex, 1);
        }

        roots.splice(insertIndex, 0, category);
    }

    async prepareGrowSidebarAudienceTree(audiences: MaxBrainAudience[]): Promise<AudienceResource[]> {
        const sortedAudiences = [...audiences].sort(MaxBrainUtils.sortCompareFn(AudienceState.sortProperties));
        const roots = this.convertListToTree(sortedAudiences);
        const defaultAudience = roots[0];

        if (!defaultAudience) {
            return roots;
        }

        let automaticCategory =
            this.extractAutomaticAudiencesCategory(roots) ?? this.extractAutomaticAudiencesCategory(defaultAudience.children || []);
        let courseCategory =
            this.extractCourseAudiencesCategory(roots) ?? this.extractCourseAudiencesCategory(defaultAudience.children || []);

        const fetchedChildren = await this.fetchAudienceChildren(defaultAudience.id);
        defaultAudience.children = this.mergeAudienceTreeChildren(
            this.withoutPromotedSidebarCategories(defaultAudience.children),
            this.withoutPromotedSidebarCategories(fetchedChildren || [])
        );

        automaticCategory = automaticCategory ?? this.extractAutomaticAudiencesCategory(defaultAudience.children || []);
        courseCategory = courseCategory ?? this.extractCourseAudiencesCategory(defaultAudience.children || []);

        const manualAudiences = defaultAudience.children || [];
        defaultAudience.children = [];
        defaultAudience.childrenCount = 0;

        let insertIndex = 1;

        if (manualAudiences.length > 0) {
            const audiencesCategory = this.createAudiencesCategory(manualAudiences);
            this.sortGrowAudienceChildren(audiencesCategory);
            this.promoteCategoryToRoot(roots, audiencesCategory, insertIndex++);
        }

        if (automaticCategory) {
            await this.hydrateAutomaticAudiencesCategory(automaticCategory);
            this.promoteCategoryToRoot(roots, automaticCategory, insertIndex++);
        }

        if (courseCategory) {
            await this.hydrateCourseAudiencesCategory(courseCategory);
            this.promoteCategoryToRoot(roots, courseCategory, insertIndex++);
        }

        return roots;
    }

    async fetchDefaultAudiences(pageSize?: number, pageIndex?: number): Promise<boolean> {
        this._store.dispatch(new FetchDefaultAudiences({ pageSize, pageIndex }));

        return await this._actionHelper.race(FetchDefaultAudiencesSuccess, FetchDefaultAudiencesFailure).toPromise();
    }

    /**
     * Fetches every default audience in a single call without mutating the paginated `defaultAudiencesList` store.
     * Used to feed callers (e.g. the default-audience picker dialog) that need the complete list independent of
     * the paginator currently rendered in the GrowDefaultSettingComponent table.
     */
    async fetchAllDefaultAudiencesPlain(): Promise<MaxBrainAudience[]> {
        const response = await firstValueFrom(this._audienceApiService.getDefaultAudiences(10000, 1));
        return response.data.map((query) => new MaxBrainAudience(query));
    }

    async fetchDefaultSettingRootAudiences(experienceId?: string | null): Promise<MaxBrainAudience[]> {
        return this.fetchAudiencesList(undefined, {
            context: 'defaultSetting',
            experienceId,
            ...EXCLUDE_AUTOMATIC_AUDIENCE_LIST_OPTIONS,
        });
    }

    async searchDefaultSettingAudiences(search: string, experienceId?: string | null): Promise<MaxBrainAudience[]> {
        return this.fetchAudiencesList(search, {
            context: 'defaultSetting',
            experienceId,
            ...EXCLUDE_AUTOMATIC_AUDIENCE_LIST_OPTIONS,
        });
    }

    async fetchDefaultSettingAudienceChildren(audienceId: string, experienceId?: string | null): Promise<MaxBrainAudience[]> {
        return this.fetchAudienceChildrenList(audienceId, {
            context: 'defaultSetting',
            experienceId,
            ...EXCLUDE_AUTOMATIC_AUDIENCE_LIST_OPTIONS,
        });
    }

    async patchDefaultAudiences(audienceIds: string[], isDefault: boolean): Promise<boolean> {
        try {
            await firstValueFrom(this._audienceApiService.patchAudienceDefaultFlags(audienceIds, isDefault));
            return true;
        } catch {
            return false;
        }
    }
}
